import {
  estadoVisual,
  etiquetaMotivo,
  nombreDeCliente,
  fmtDuracion,
  horaMadrid,
  type EstadoVisual,
} from '@/lib/llamadas/presentacion';
import type { LlamadaFeedRow } from '@/types/llamadas';

// Fila del feed (prototipo iter5): barra de estado a la izquierda, quién +
// motivo, badge de estado y hora · duración en mono. Focusable y seleccionable
// con Enter/Espacio (accesibilidad básica, spec §10).

const BADGE: Record<EstadoVisual, { texto: string; color: string; bg: string; anim?: boolean }> = {
  en_curso: {
    texto: 'En curso',
    color: 'var(--status-live)',
    bg: 'var(--status-live-bg)',
    anim: true,
  },
  procesando: { texto: 'Procesando', color: 'var(--text-2)', bg: 'var(--surface-3)' },
  resuelta: {
    texto: 'Resuelta por IA',
    color: 'var(--status-ok-ink)',
    bg: 'var(--status-ok-bg)',
  },
  escalada: { texto: 'Escalada', color: 'var(--status-esc-ink)', bg: 'var(--status-esc-bg)' },
};

export function FilaLlamada({
  llamada,
  seleccionada,
  onSeleccionar,
}: {
  llamada: LlamadaFeedRow;
  seleccionada: boolean;
  onSeleccionar: () => void;
}) {
  const estado = estadoVisual(llamada);
  const badge = BADGE[estado];
  // «¿Hay cliente?» se contesta con `id_cliente`, igual que en el Excel, en la tabla del
  // cuadro y en la ficha de al lado. Decidirlo por la veracidad de la razón social pintaba
  // esta fila como NO identificada mientras la ficha de la misma pantalla sí nombraba al
  // cliente por su id de Odoo: la sexta superficie de la misma pregunta.
  const nombre =
    llamada.id_cliente === null
      ? null
      : nombreDeCliente(llamada.cliente?.razon_social ?? null, llamada.cliente_id_odoo);
  // Veracidad y no `??`: `upsert_llamada` guarda el teléfono pasado por `_norm_tel`, que
  // quita todo lo que no sea dígito o `+`, así que un `anonymous` —lo que manda el SIP con
  // el número retenido— se guarda como cadena VACÍA, no como nulo. Con `??` el hueco salía
  // en blanco mientras el mismo dato, dos líneas más allá, decía «número oculto».
  const telefono = llamada.telefono_origen || 'Número oculto';
  // Veracidad y no `!= null`: un motivo vacío es «sin motivo», como lo tratan el Excel
  // y los chips. Con `!= null` la fila se quedaba sin nada que decir en ese hueco.
  const motivo = llamada.motivo ? etiquetaMotivo(llamada.motivo) : 'Motivo pendiente';

  return (
    <div
      data-testid="fila-llamada"
      role="button"
      tabIndex={0}
      aria-pressed={seleccionada}
      onClick={onSeleccionar}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSeleccionar();
        }
      }}
      className={`relative flex cursor-pointer items-center gap-3 rounded-[var(--radius-sm)] border px-3 py-2.5 focus-visible:outline-none focus-visible:shadow-[var(--ring)] ${
        seleccionada
          ? 'border-[var(--orange)] bg-[var(--orange-bg)]'
          : 'border-transparent hover:bg-[var(--surface-2)]'
      }`}
    >
      <span
        className="h-9 w-[3px] shrink-0 rounded-full"
        style={{ background: badge.color }}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        {nombre ? (
          <>
            <div className="truncate text-[13px] font-semibold text-[var(--navy)]">{nombre}</div>
            <div className="truncate text-xs text-[var(--text-dim)]">
              {motivo} · <span className="mono">{telefono}</span>
            </div>
          </>
        ) : (
          <>
            <div className="mono truncate text-[13px] font-semibold text-[var(--navy)]">
              {telefono}
            </div>
            <div className="truncate text-xs text-[var(--text-dim)]">{motivo}</div>
          </>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span
          className="flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={{ color: badge.color, background: badge.bg }}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${badge.anim ? 'animate-[blip_1.2s_infinite]' : ''}`}
            style={{ background: badge.color }}
          />
          {badge.texto}
        </span>
        <span className="mono text-[11.5px] text-[var(--text-mute)]">
          {horaMadrid(llamada.inicio)} · {fmtDuracion(llamada.duracion_s)}
        </span>
      </div>
    </div>
  );
}
